// Mission step 1: General information form
// Handles name, description, duration, and service selection
// CREATED: Part of unified MissionModal architecture

import type { Service } from '../../lib/services';
import { DurationPicker } from '../ui/DurationPicker';
import { CustomSelect } from '../ui/CustomSelect';

interface MissionStep1InfoProps {
  name: string;
  onNameChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  duration: number | null;
  onDurationChange: (value: number | null) => void;
  serviceId: string;
  onServiceIdChange: (value: string) => void;
  services: Service[];
  loading: boolean;
}

export function MissionStep1Info({
  name,
  onNameChange,
  description,
  onDescriptionChange,
  duration,
  onDurationChange,
  serviceId,
  onServiceIdChange,
  services,
  loading,
}: MissionStep1InfoProps) {
  // Character counter helper - only show when >= 80% of limit
  const showCharacterCounter = (current: number, max: number) => {
    const percentage = (current / max) * 100;
    if (percentage < 80) return null;

    const color = percentage >= 100 ? 'text-red-600' : percentage >= 90 ? 'text-orange-600' : 'text-gray-500';

    return (
      <p className={`mt-1 text-xs ${color}`}>
        {current}/{max} caractères
      </p>
    );
  };

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Name */}
      <div>
        <label htmlFor="template-name" className="block text-sm font-medium text-gray-700 mb-2">
          Nom de la mission <span className="text-red-500">*</span>
        </label>
        <input
          id="template-name"
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          required
          disabled={loading}
          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
          maxLength={100}
        />
        {showCharacterCounter(name.length, 100)}
      </div>

      {/* Description */}
      <div>
        <label htmlFor="template-description" className="block text-sm font-medium text-gray-700 mb-2">
          Description <span className="text-gray-500 font-normal">(optionnelle)</span>
        </label>
        <textarea
          id="template-description"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          disabled={loading}
          rows={3}
          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed resize-none"
          maxLength={500}
        />
        {showCharacterCounter(description.length, 500)}
      </div>

      {/* Estimated duration & Service selection - Side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Estimated duration */}
        <div>
          <DurationPicker
            value={duration}
            onChange={onDurationChange}
            label="Durée estimée"
            placeholder="Sélectionnez une durée"
            required
            disabled={loading}
            minDuration={15}
            maxDuration={480}
            step={15}
          />
        </div>

        {/* Service selection */}
        <div>
          <label htmlFor="template-service" className="block text-sm font-medium text-gray-700 mb-2">
            Service <span className="text-red-500">*</span>
          </label>
          <CustomSelect
            value={serviceId}
            onChange={onServiceIdChange}
            options={services.map(service => ({
              value: service.id,
              label: service.name,
              color: service.color
            }))}
            disabled={loading}
            placeholder="Sélectionnez un service"
            renderOption={(option) => (
              <div className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0 border border-gray-300"
                  style={{ backgroundColor: option.color }}
                />
                <span>{option.label}</span>
              </div>
            )}
            renderValue={(option) => (
              <div className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0 border border-gray-300"
                  style={{ backgroundColor: option.color }}
                />
                <span>{option.label}</span>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
}
